import { localCatalog } from '../data/catalog'
import { nowStamp } from '../core/format/dates'
import { sumCents, toCents } from '../core/format/currency'
import { ApiError } from '../core/api/http'
import { db, type OrderLine, type OrderRow } from './db'
import { bookStock, toItem } from './stockBackend'
import { giftStockBookings } from '../core/rules/giftTiers'
import { giftTiers } from '../data/gifts'
import { policy } from '../data/policies'
import { recordCustomerSource } from './acquisition'
import { normalizeSource } from '../core/rules/acquisition'
import { resolveFeatures } from '../core/rules/features'
import { isDigital, pricedLines } from './pricing'
import { demoId, reply } from './reply'
import { adjustStock } from './salesBackend'
import { checkVoucherAnswer, issueOrderVouchers, redeemVoucher, usableVoucher, voucherShare } from './voucherBackend'
import { attachOrderKuur } from './kuurOrders'
import { isKuurProductId } from '../core/rules/kuur'
import { assertBookable, demoAvailability, durationOf } from './agendaBackend'
import type { ShopBackend } from '../core/types/backend'

/** Het antwoord op een bestelling, in centen — zoals het contract het vraagt. */
const orderAnswer = (orderId: string, totalEuro: number, shippingEuro: number, gift: string | null, voucherCents = 0) => ({
  orderId,
  totalCents: toCents(totalEuro),
  voucherCents,
  shippingCents: toCents(shippingEuro),
  // De demo betaalt direct. Met Mollie: een betaallink, behalve als de bon alles dekt (dan ook `null`).
  paymentUrl: null,
  gift,
})

/**
 * DEMO — de webshop-kant van de backend. Bestellingen en aanvragen komen in
 * dezelfde tabellen als waar Mijn omgeving en het beheer uit lezen.
 */
/** Voor de terugkeerpagina (via api.demoOrderNote): waarom een bestelling in een andere browser niet te vinden is. */
export const demoOrderReturnNote = 'In de demo staan bestellingen alleen in de browser waarin je bestelde. Met de echte server werkt de link overal.'

/** Voor de demo-knop na aanmelden (via api.demoNewsletterLink): de link uit de bevestigingsmail. */
export const demoNewsletterConfirmPath = '/nieuwsbrief/bevestig?token=demo'

/**
 * De demo-winkel haalt geen GET /site op. Zo volgen de onderdelen (fotodagboek, abonnement) toch
 * wat het beheer opsloeg, na herladen, zoals met de echte server.
 */
export const demoSavedFeatures = (): unknown => db.savedFeatures()

/** Token dat in de demo altijd als verlopen geldt. */
const DEMO_EXPIRED_TOKEN = 'verlopen'

/**
 * Mag de bedankpagina de herkomst vragen? Niet als het onderdeel uit staat, de order al een antwoord
 * heeft of de klant (account of e-mailadres) al een herkomst heeft.
 */
function askSourceFor(row: OrderRow): boolean {
  if (row.payment.status !== 'betaald' || row.acquisitionSource) return false
  if (!resolveFeatures(db.savedFeatures()).acquisitionQuestion) return false
  const account = row.customerId ? db.account(row.customerId) : db.accountByEmail(row.request.email)
  return !account?.customer.acquisitionSource
}

/**
 * Zoals de server: per bereikte trede met een gekoppeld voorraadartikel één stuk afschrijven
 * (oorzaak `cadeau`), met de order als verwijzing. De staffel telt over de producten die op de post gaan.
 */
function bookGiftStock(lines: OrderLine[], choices: Record<string, string> | undefined, orderId: string) {
  const subtotal = sumCents(lines.filter((l) => !isDigital(l.productId))) / 100
  giftStockBookings(giftTiers, policy.freeShippingFrom, subtotal, choices).forEach((g) => {
    if (!toItem(g.itemId)) return
    bookStock(g.itemId, { reason: 'afschrijven', location: 'webshop', qty: -1, writeOffCause: 'cadeau', ref: orderId, by: 'Webshop', note: 'Cadeau bij bestelling' })
  })
}

export const demoShopBackend: ShopBackend = {
  fetchCatalog: async () => localCatalog,
  fetchSite: async () => null,
  fetchPages: async () => null,

  async submitOrder(order, preview) {
    // Idempotent op requestId, zoals de backend het moet doen.
    const seen = db.orderByRequest(order.requestId)
    // Zelfde sleutel, ander bedrag: geen stille herhaling van de oude order (LARAVEL.md §9).
    if (seen && seen.total != null && toCents(seen.total) - (seen.voucherCents ?? 0) !== order.expectedTotalCents) {
      throw new ApiError('Deze bestelling is al verstuurd met andere gegevens.', 409, undefined, 'idempotency_conflict')
    }
    if (seen) return reply(orderAnswer(seen.id, seen.total ?? preview.total, seen.shipping, seen.gift, seen.voucherCents), 300)

    // Cadeaubon: dezelfde controle als POST /vouchers/check (422 `voucher_*`), en nooit meer
    // eraf dan er te betalen is. Het verwachte bedrag is wat er ná de bon overblijft.
    const voucher = order.voucherCode?.trim() ? usableVoucher(order.voucherCode) : null
    const voucherCents = voucher ? voucherShare(voucher, toCents(preview.total)) : 0

    // Zoals de server: wijkt het verwachte totaal af van wat hij zelf rekent, dan niets plaatsen
    // en niets onthouden onder de requestId (LARAVEL.md §9). De demo rekent met de preview.
    if (order.expectedTotalCents !== toCents(preview.total) - voucherCents) {
      throw new ApiError('De prijzen zijn intussen veranderd. Kijk je bestelling nog even na.', 409, undefined, 'total_mismatch')
    }

    // Zoals de server: kuur vooruit betalen uit = niet te koop (422), ook niet via een oude mand.
    if (!resolveFeatures(db.savedFeatures()).prepaidKuur && order.lines.some((l) => isKuurProductId(l.productId))) {
      throw new ApiError('Een kuur vooruit betalen kan nu niet. Haal hem uit je winkelmand.', 422, { lines: 'Kuur vooruit betalen staat uit.' }, 'kuur_off')
    }

    const account = db.accountByEmail(order.email)
    const { firstName, lastName, city } = order.address
    const lines = pricedLines(order.lines, order.address.country)
    // In centen: anders geeft 84,5 − 79,55 een verzendbedrag als 4,950000000000003.
    const shipping = Math.max(0, toCents(preview.total) - sumCents(lines)) / 100
    const id = demoId('BB')
    // Een gekochte kuur komt op het account (vooruit betaald; met Mollie: bij de betaalde webhook).
    const hasKuur = lines.some((l) => isKuurProductId(l.productId))
    const kuurLinked = hasKuur && attachOrderKuur({ customerId: account?.customer.id, lines })
    // Niets om te versturen (alleen cadeaubonnen/kuren): meteen afgehandeld, dus niet in "in te pakken".
    // Een kuur die niet automatisch gekoppeld kon worden blijft juist in de inbox staan, zodat Sarah hem vastlegt.
    const digitalOnly = lines.every((l) => isDigital(l.productId))
    db.putOrder({
      id,
      requestId: order.requestId,
      receivedAt: nowStamp(),
      customerId: account?.customer.id,
      name: `${firstName} ${lastName}`.trim() || order.email,
      city,
      fulfilment: digitalOnly && (!hasKuur || kuurLinked) ? 'bezorgd' : 'nieuw',
      request: {
        email: order.email,
        address: order.address,
        lines: order.lines,
        code: order.code,
        skinNote: order.skinNote,
        refillReminder: order.refillReminder,
      },
      // De cadeaukeuze is al verwerkt in `gift` (preview); de ruwe keuzes hoeven niet bewaard.
      // De demo betaalt direct. Met Mollie begint dit op 'open' en zet de webhook hem op 'betaald'.
      payment: { status: 'betaald', method: order.paymentMethod },
      gift: preview.gift,
      lines,
      shipping,
      total: preview.total,
      viaSubscription: order.lines.some((l) => l.subscribe),
      ...(voucherCents ? { voucherCents } : {}),
      ...(hasKuur && !kuurLinked ? { kuurUnlinked: true } : {}),
    })
    // In één transactie met het plaatsen: de bon gaat af (met de order als verwijzing).
    if (voucher) redeemVoucher(voucher.id, voucherCents, id)
    // De demo betaalt direct, dus het nummer komt meteen (met Mollie: bij de betaalde webhook).
    db.numberOrder(id)
    // Gekochte cadeaubonnen krijgen nu hun code (met Mollie: bij de betaalde webhook).
    issueOrderVouchers({ id, name: `${firstName} ${lastName}`.trim() || order.email, lines })
    // Zoals de server: voorraad gaat bij plaatsen omlaag (gereserveerd), bij annuleren weer omhoog.
    adjustStock(order.lines, -1, id)
    // Bij betalen (met Mollie: de betaalde webhook): de cadeaus uit de staffel gaan van de webshopvoorraad af.
    bookGiftStock(lines, order.giftChoices, id)
    return reply(orderAnswer(id, preview.total, shipping, preview.gift, voucherCents), 1100)
  },

  /**
   * De demo betaalt direct, dus de status is meteen 'betaald'. Met Mollie zet de webhook hem.
   * Het token uit de link negeert de demo: bestellingen staan hier toch alleen in deze browser.
   */
  async fetchOrderStatus(orderId) {
    const row = db.orders().find((o) => o.id === orderId)
    if (!row) throw new ApiError('Deze bestelling kennen we niet.', 404)
    return reply(
      {
        orderId: row.id,
        status: row.payment.status,
        totalCents: row.total !== undefined ? toCents(row.total) : sumCents(row.lines) + toCents(row.shipping),
        shippingCents: toCents(row.shipping),
        voucherCents: row.voucherCents ?? 0,
        gift: row.gift,
        // Alleen een betaalde bestelling heeft een bestelnummer.
        orderNumber: row.payment.status === 'betaald' && row.number ? String(row.number) : null,
        askSource: askSourceFor(row),
      },
      400,
    )
  },

  /** Zoals de server: één keer per betaalde order; alleen het eerste antwoord komt bij de klant. */
  async submitOrderSource(orderId, _token, { source }) {
    const row = db.orders().find((o) => o.id === orderId)
    if (!row || row.payment.status !== 'betaald') throw new ApiError('Deze bestelling kennen we niet.', 404)
    const clean = normalizeSource(source)
    if (clean && !row.acquisitionSource) {
      db.changeOrder(orderId, (o) => ({ ...o, acquisitionSource: clean }))
      recordCustomerSource(row.customerId ?? db.accountByEmail(row.request.email)?.customer.id, clean, 'bestelling')
    }
    return reply({ ok: true as const }, 350)
  },

  /**
   * Met Mollie: een nieuwe betaallink voor dezelfde order. De demo heeft geen Mollie en betaalt
   * meteen, en stuurt dan naar de terugkeerpagina, zoals Mollie na een gelukte betaling doet.
   * Hoort de order bij een mislukte abonnementsbetaling, dan verdwijnt die melding (de webhook).
   */
  async retryPayment(orderId) {
    const row = db.orders().find((o) => o.id === orderId)
    if (!row) throw new ApiError('Deze bestelling kennen we niet.', 404)
    if (row.payment.status === 'betaald') {
      await reply(null, 300)
      throw new ApiError('Deze bestelling is al betaald.', 409, undefined, 'already_paid')
    }
    db.changeOrder(orderId, (o) => ({ ...o, payment: { ...o.payment, status: 'betaald' } }))
    db.numberOrder(orderId)
    if (row.customerId) {
      db.changeAccount(row.customerId, (a) => {
        const sub = a.subscription
        if (!sub || sub.paymentIssue?.orderId !== orderId) return [a, null]
        const { paymentIssue: _paid, ...subscription } = sub
        return [{ ...a, subscription }, null]
      })
    }
    return reply({ paymentUrl: `/bestelling/${encodeURIComponent(orderId)}?t=demo` }, 600)
  },

  /** Hoort het e-mailadres bij een klant, dan komt de aanvraag in haar dossier; anders als gast. */
  async submitBooking({ requestId, ...booking }) {
    // Idempotent op requestId, zoals bij bestellen.
    const seen = db.appointmentByRequest(requestId)
    if (seen) return reply({ bookingId: seen.id }, 300)
    // Zoals de server (met een lock): buiten de tijden = 422 `outside_hours`, intussen bezet = 409 `slot_taken`.
    const performer = assertBookable({
      date: booking.date,
      time: booking.time,
      treatmentSlug: booking.treatmentSlug,
      durationMinutes: durationOf(booking.treatmentSlug),
      ...(booking.staffId ? { preferredStaffId: booking.staffId } : {}),
    })
    const account = db.accountByEmail(booking.email)
    // Zoals de server: Sarah zette "Niet online laten boeken" aan. De reden blijft intern.
    if (account && db.bookingBlock(account.customer.id)) {
      throw new ApiError('Online boeken lukt nu niet. Bel of app de salon.', 409, undefined, 'booking_blocked')
    }
    const online = localCatalog.treatments.find((t) => t.slug === booking.treatmentSlug)?.online
    // Alleen bij een eerste bezoek, en alleen als ze het invulde; ongeldig = genegeerd.
    const source = booking.firstVisit ? normalizeSource(booking.acquisitionSource) : null
    if (account && source) recordCustomerSource(account.customer.id, source, 'afspraak')
    const id = account ? demoId('af').toLowerCase() : demoId('AF')
    db.addAppointment({
      id,
      requestId,
      staffId: performer,
      receivedAt: nowStamp(),
      treatmentSlug: booking.treatmentSlug,
      date: booking.date,
      time: booking.time,
      kind: online ? 'video' : 'salon',
      status: 'aanvraag',
      request: 'nieuw',
      ...(account
        ? { customerId: account.customer.id }
        : { guest: { name: booking.name, email: booking.email, firstVisit: booking.firstVisit, ...(source ? { acquisitionSource: source } : {}) } }),
    })
    return reply({ bookingId: id }, 700)
  },

  fetchAvailability: (treatmentSlug, filter) => demoAvailability(treatmentSlug, filter),

  async submitQuizLead({ requestId, ...lead }) {
    db.addLead({ id: `lead-${requestId}`, receivedAt: nowStamp(), lead })
    return reply({ ok: true as const }, 700)
  },

  async subscribeNewsletter({ email }) {
    db.addSignup(email)
    return reply({ ok: true as const }, 700)
  },

  /** Elk niet-leeg token lukt, behalve `verlopen` (zo test je de verlopen-link-melding). */
  async confirmNewsletter({ token }) {
    const t = token.trim()
    if (!t || t === DEMO_EXPIRED_TOKEN) {
      await reply(null, 500)
      throw new ApiError('Deze link is verlopen of al gebruikt.', 422, undefined, 'link_expired')
    }
    return reply({ ok: true as const }, 600)
  },

  /** Zoals de server: alleen een bon "ook voor producten", niet verlopen en niet leeg. Boekt niets af. */
  async checkVoucher({ code }) {
    try {
      const row = usableVoucher(code)
      return reply(checkVoucherAnswer(row), 500)
    } catch (err) {
      await reply(null, 500)
      throw err
    }
  },
}
